import type { ReactElement } from 'react';

import type { LmsSearchRow } from '../types';
import { skeletonLine } from '@/components/ui/skeleton';

// Resultados de la búsqueda en el LMS como opciones de radio. Un documento del LMS
// que ya usa otro documento vivo se muestra pero no se puede elegir.

export interface LmsResultsState {
  data: LmsSearchRow[] | undefined;
  isFetching: boolean;
  isError: boolean;
  refetch: () => unknown;
}

export function LmsResults({
  state,
  entryId,
  selected,
  onSelect,
}: {
  state: LmsResultsState;
  entryId: string;
  selected: number | null;
  onSelect: (row: LmsSearchRow) => void;
}): ReactElement | null {
  if (state.isError) {
    return (
      <div className="fld-error" role="alert">
        No se pudo consultar el LMS.
        <button
          type="button"
          className="btn mut sm"
          aria-label="Reintentar la búsqueda"
          onClick={() => void state.refetch()}
        >
          Reintentar
        </button>
      </div>
    );
  }
  if (state.isFetching && state.data === undefined) {
    return (
      <div aria-busy="true" aria-label="Buscando en el LMS" style={{ display: 'grid', gap: 8 }}>
        <div className={skeletonLine({ width: 90 })} />
        <div className={skeletonLine({ width: 70 })} />
        <div className={skeletonLine({ width: 80 })} />
      </div>
    );
  }
  if (state.data === undefined) {
    return null;
  }
  if (state.data.length === 0) {
    return (
      <p className="soft" style={{ margin: 0, fontSize: 13 }}>
        El LMS no tiene documentos que coincidan en esta empresa.
      </p>
    );
  }
  return (
    <fieldset className="fld" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="fld-lab">Resultados</legend>
      <div className="doc-picker" style={{ flexDirection: 'column', flexWrap: 'nowrap' }}>
        {state.data.map((row) => {
          const owner = row.linkedEntry;
          const takenByOther = owner !== null && owner.id !== entryId;
          const linkedHere = owner !== null && owner.id === entryId;
          return (
            <label key={row.id} className="doc-opt">
              <input
                type="radio"
                name="lms-document"
                checked={selected === row.id}
                disabled={owner !== null}
                onChange={() => onSelect(row)}
              />
              <span className="code">{row.code}</span> {row.title}
              <span className="soft" style={{ fontSize: 12 }}>
                {` · ${row.category} · v${row.currentVersion}`}
                {!row.hasFile && ' · Sin archivo'}
              </span>
              {takenByOther && <span className="fld-err">Ya vinculado a {owner.code}</span>}
              {linkedHere && <span className="soft">Es el archivo actual</span>}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
